import { lazy } from 'react';
import {
  LayoutDashboard,
  BarChart3,
  Calendar,
  Trophy,
  Building2,
  MapPin,
  Ticket,
  UserCheck,
  Crown,
  Newspaper,
  Vote,
  Wallet,
  ShoppingBag,
  Settings,
  Briefcase,
  Users,
  Shield,
  Car,
  GraduationCap,
  Building,
  Target,
  Heart,
  Handshake,
  FileText,
  Image,
  FileCheck,
  ClipboardCheck,
  Scale,
  AlertTriangle
} from 'lucide-react';

// Lazy load all modules for performance
const EventsModule = lazy(() => import('../components/dashboard/EventsModule'));
const OrganizationsModule = lazy(() => import('../components/dashboard/OrganizationsModule'));
const TicketingModule = lazy(() => import('../components/dashboard/TicketingModule'));
const AccreditationModule = lazy(() => import('../components/dashboard/AccreditationModule'));
const TournamentsModule = lazy(() => import('../components/dashboard/TournamentsModule'));
const FacilitiesModule = lazy(() => import('../components/dashboard/FacilitiesModule'));
const AnalyticsModule = lazy(() => import('../components/dashboard/AnalyticsModule'));
const MembershipModule = lazy(() => import('../components/dashboard/MembershipModule'));
const NewsCMSModule = lazy(() => import('../components/dashboard/NewsCMSModule'));
const FanEngagementModule = lazy(() => import('../components/dashboard/FanEngagementModule'));
const WalletModule = lazy(() => import('../components/dashboard/WalletModule'));
const MarketplaceModule = lazy(() => import('../components/dashboard/MarketplaceModule'));
const GovernanceModule = lazy(() => import('../components/dashboard/GovernanceModule'));
const SponsorshipModule = lazy(() => import('../components/dashboard/SponsorshipModule'));
const WorkforceModule = lazy(() => import('../components/dashboard/WorkforceModule'));
const VisitorModule = lazy(() => import('../components/dashboard/VisitorModule'));
const ParkingModule = lazy(() => import('../components/dashboard/ParkingModule'));
const TalentModule = lazy(() => import('../components/dashboard/TalentModule'));
const SchoolSportsModule = lazy(() => import('../components/dashboard/SchoolSportsModule'));
const AcademyModule = lazy(() => import('../components/dashboard/AcademyModule'));
const CorporateSportsModule = lazy(() => import('../components/dashboard/CorporateSportsModule'));
const AdminModule = lazy(() => import('../components/dashboard/AdminModule'));
const MediaModule = lazy(() => import('../components/dashboard/MediaModule'));
const MedicalModule = lazy(() => import('../components/dashboard/MedicalModule'));
const SettingsModule = lazy(() => import('../components/dashboard/SettingsModule'));
const CMSModule = lazy(() => import('../components/dashboard/CMSModule'));
const LicensingModule = lazy(() => import('../components/dashboard/LicensingModule'));

// User roles in the system
export const USER_ROLES = {
  SUPER_ADMIN: 'super_admin',
  PLATFORM_ADMIN: 'platform_admin',
  FEDERATION_ADMIN: 'federation_admin',
  CLUB_ADMIN: 'club_admin',
  ACADEMY_ADMIN: 'academy_admin',
  CORPORATE_ADMIN: 'corporate_admin',
  EVENT_MANAGER: 'event_manager',
  FACILITY_MANAGER: 'facility_manager',
  VENDOR: 'vendor',
  FAN: 'fan',
  ATHLETE: 'athlete',
  COACH: 'coach',
  // Licensing-specific roles
  LICENSING_OFFICER: 'licensing_officer',
  COMPLIANCE_INSPECTOR: 'compliance_inspector',
  REGULATORY_ADMIN: 'regulatory_authority_admin',
  LICENSING_COMMITTEE: 'licensing_committee_member',
  APPEALS_COMMITTEE: 'appeals_committee_member'
};

// Organization types
export const ORG_TYPES = {
  FEDERATION: 'Federation',
  CLUB: 'Club',
  ACADEMY: 'Academy',
  SCHOOL: 'School',
  FACILITY: 'Facility',
  CORPORATE: 'Corporate',
  GOVERNMENT: 'Government'
};

// Module categories for grouping
export const MODULE_CATEGORIES = {
  CORE: 'Core',
  MANAGEMENT: 'Management',
  ENGAGEMENT: 'Engagement & Commerce',
  OPERATIONS: 'Operations',
  LICENSING: 'Licensing & Compliance'
};

// Module registry - Central configuration for all dashboard modules
export const moduleRegistry = {
  // Core modules
  overview: {
    id: 'overview',
    name: 'Dashboard',
    icon: LayoutDashboard,
    route: '/dashboard',
    exactRoute: true,
    component: null, // Uses DashboardOverview inline
    category: MODULE_CATEGORIES.CORE,
    requiredRoles: Object.values(USER_ROLES),
    description: 'Overview and quick stats'
  },
  analytics: {
    id: 'analytics',
    name: 'Analytics',
    icon: BarChart3,
    route: '/dashboard/analytics',
    component: AnalyticsModule,
    category: MODULE_CATEGORIES.CORE,
    requiredRoles: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.PLATFORM_ADMIN,
      USER_ROLES.FEDERATION_ADMIN,
      USER_ROLES.CLUB_ADMIN,
      USER_ROLES.ACADEMY_ADMIN,
      USER_ROLES.CORPORATE_ADMIN,
      USER_ROLES.EVENT_MANAGER,
      USER_ROLES.FACILITY_MANAGER
    ],
    description: 'Charts, trends, and insights'
  },
  
  // Management modules
  events: {
    id: 'events',
    name: 'Events',
    icon: Calendar,
    route: '/dashboard/events',
    component: EventsModule,
    category: MODULE_CATEGORIES.MANAGEMENT,
    requiredRoles: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.PLATFORM_ADMIN,
      USER_ROLES.FEDERATION_ADMIN,
      USER_ROLES.CLUB_ADMIN,
      USER_ROLES.ACADEMY_ADMIN,
      USER_ROLES.CORPORATE_ADMIN,
      USER_ROLES.EVENT_MANAGER
    ],
    description: 'Create and manage events'
  },
  tournaments: {
    id: 'tournaments',
    name: 'Tournaments',
    icon: Trophy,
    route: '/dashboard/tournaments',
    component: TournamentsModule,
    category: MODULE_CATEGORIES.MANAGEMENT,
    requiredRoles: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.PLATFORM_ADMIN,
      USER_ROLES.FEDERATION_ADMIN,
      USER_ROLES.CLUB_ADMIN,
      USER_ROLES.EVENT_MANAGER
    ],
    description: 'Tournament brackets and scores'
  },
  organizations: {
    id: 'organizations',
    name: 'Organizations',
    icon: Building2,
    route: '/dashboard/organizations',
    component: OrganizationsModule,
    category: MODULE_CATEGORIES.MANAGEMENT,
    requiredRoles: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.PLATFORM_ADMIN,
      USER_ROLES.FEDERATION_ADMIN
    ],
    description: 'Manage federations, clubs, academies'
  },
  facilities: {
    id: 'facilities',
    name: 'Facilities',
    icon: MapPin,
    route: '/dashboard/facilities',
    component: FacilitiesModule,
    category: MODULE_CATEGORIES.MANAGEMENT,
    requiredRoles: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.PLATFORM_ADMIN,
      USER_ROLES.FEDERATION_ADMIN,
      USER_ROLES.CLUB_ADMIN,
      USER_ROLES.FACILITY_MANAGER
    ],
    description: 'Facility booking and management'
  },
  governance: {
    id: 'governance',
    name: 'Governance',
    icon: Shield,
    route: '/dashboard/governance',
    component: GovernanceModule,
    category: MODULE_CATEGORIES.MANAGEMENT,
    requiredRoles: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.PLATFORM_ADMIN,
      USER_ROLES.FEDERATION_ADMIN
    ],
    description: 'Board meetings, policies, compliance'
  },
  workforce: {
    id: 'workforce',
    name: 'Workforce',
    icon: Users,
    route: '/dashboard/workforce',
    component: WorkforceModule,
    category: MODULE_CATEGORIES.MANAGEMENT,
    requiredRoles: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.PLATFORM_ADMIN,
      USER_ROLES.FEDERATION_ADMIN,
      USER_ROLES.CLUB_ADMIN,
      USER_ROLES.FACILITY_MANAGER
    ],
    description: 'Staff and volunteer management'
  },
  visitors: {
    id: 'visitors',
    name: 'Visitors',
    icon: UserCheck,
    route: '/dashboard/visitors',
    component: VisitorModule,
    category: MODULE_CATEGORIES.MANAGEMENT,
    requiredRoles: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.FACILITY_MANAGER,
      USER_ROLES.EVENT_MANAGER
    ],
    description: 'Visitor check-in and tracking'
  },
  parking: {
    id: 'parking',
    name: 'Parking',
    icon: Car,
    route: '/dashboard/parking',
    component: ParkingModule,
    category: MODULE_CATEGORIES.MANAGEMENT,
    requiredRoles: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.FACILITY_MANAGER
    ],
    description: 'Parking management and permits'
  },
  sponsorship: {
    id: 'sponsorship',
    name: 'Sponsorship',
    icon: Handshake,
    route: '/dashboard/sponsorship',
    component: SponsorshipModule,
    category: MODULE_CATEGORIES.MANAGEMENT,
    requiredRoles: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.PLATFORM_ADMIN,
      USER_ROLES.FEDERATION_ADMIN,
      USER_ROLES.CLUB_ADMIN,
      USER_ROLES.EVENT_MANAGER
    ],
    description: 'Sponsor partnerships and assets'
  },
  
  // Engagement & Commerce modules
  ticketing: {
    id: 'ticketing',
    name: 'Ticketing',
    icon: Ticket,
    route: '/dashboard/ticketing',
    component: TicketingModule,
    category: MODULE_CATEGORIES.ENGAGEMENT,
    requiredRoles: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.PLATFORM_ADMIN,
      USER_ROLES.FEDERATION_ADMIN,
      USER_ROLES.CLUB_ADMIN,
      USER_ROLES.EVENT_MANAGER,
      USER_ROLES.FAN
    ],
    description: 'Event tickets and QR codes'
  },
  accreditation: {
    id: 'accreditation',
    name: 'Accreditation',
    icon: UserCheck,
    route: '/dashboard/accreditation',
    component: AccreditationModule,
    category: MODULE_CATEGORIES.ENGAGEMENT,
    requiredRoles: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.PLATFORM_ADMIN,
      USER_ROLES.FEDERATION_ADMIN,
      USER_ROLES.CLUB_ADMIN,
      USER_ROLES.EVENT_MANAGER
    ],
    description: 'Passes and access control'
  },
  membership: {
    id: 'membership',
    name: 'Membership',
    icon: Crown,
    route: '/dashboard/membership',
    component: MembershipModule,
    category: MODULE_CATEGORIES.ENGAGEMENT,
    requiredRoles: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.PLATFORM_ADMIN,
      USER_ROLES.FEDERATION_ADMIN,
      USER_ROLES.CLUB_ADMIN,
      USER_ROLES.ACADEMY_ADMIN,
      USER_ROLES.CORPORATE_ADMIN,
      USER_ROLES.FAN
    ],
    description: 'Member tiers and loyalty points'
  },
  news: {
    id: 'news',
    name: 'News CMS',
    icon: Newspaper,
    route: '/dashboard/news',
    component: NewsCMSModule,
    category: MODULE_CATEGORIES.ENGAGEMENT,
    requiredRoles: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.PLATFORM_ADMIN,
      USER_ROLES.FEDERATION_ADMIN,
      USER_ROLES.CLUB_ADMIN
    ],
    description: 'Publish articles and updates'
  },
  engagement: {
    id: 'engagement',
    name: 'Fan Engagement',
    icon: Vote,
    route: '/dashboard/engagement',
    component: FanEngagementModule,
    category: MODULE_CATEGORIES.ENGAGEMENT,
    requiredRoles: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.PLATFORM_ADMIN,
      USER_ROLES.FEDERATION_ADMIN,
      USER_ROLES.CLUB_ADMIN,
      USER_ROLES.FAN
    ],
    description: 'Polls and fan interactions'
  },
  wallet: {
    id: 'wallet',
    name: 'Wallet',
    icon: Wallet,
    route: '/dashboard/wallet',
    component: WalletModule,
    category: MODULE_CATEGORIES.ENGAGEMENT,
    requiredRoles: Object.values(USER_ROLES),
    description: 'Digital wallet and transactions'
  },
  marketplace: {
    id: 'marketplace',
    name: 'Marketplace',
    icon: ShoppingBag,
    route: '/dashboard/marketplace',
    component: MarketplaceModule,
    category: MODULE_CATEGORIES.ENGAGEMENT,
    requiredRoles: Object.values(USER_ROLES),
    description: 'Sports merchandise and equipment'
  },
  talent: {
    id: 'talent',
    name: 'Talent Discovery',
    icon: Target,
    route: '/dashboard/talent',
    component: TalentModule,
    category: MODULE_CATEGORIES.ENGAGEMENT,
    requiredRoles: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.PLATFORM_ADMIN,
      USER_ROLES.FEDERATION_ADMIN,
      USER_ROLES.CLUB_ADMIN,
      USER_ROLES.ACADEMY_ADMIN,
      USER_ROLES.COACH,
      USER_ROLES.ATHLETE
    ],
    description: 'Scout and track talent'
  },
  
  // Specialized modules
  schoolSports: {
    id: 'schoolSports',
    name: 'School Sports',
    icon: GraduationCap,
    route: '/dashboard/school-sports',
    component: SchoolSportsModule,
    category: MODULE_CATEGORIES.OPERATIONS,
    requiredRoles: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.PLATFORM_ADMIN,
      USER_ROLES.FEDERATION_ADMIN
    ],
    description: 'School sports programs'
  },
  academy: {
    id: 'academy',
    name: 'Academy',
    icon: GraduationCap,
    route: '/dashboard/academy',
    component: AcademyModule,
    category: MODULE_CATEGORIES.OPERATIONS,
    requiredRoles: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.PLATFORM_ADMIN,
      USER_ROLES.ACADEMY_ADMIN,
      USER_ROLES.COACH
    ],
    description: 'Training programs and players'
  },
  corporateSports: {
    id: 'corporateSports',
    name: 'Corporate Sports',
    icon: Briefcase,
    route: '/dashboard/corporate-sports',
    component: CorporateSportsModule,
    category: MODULE_CATEGORIES.OPERATIONS,
    requiredRoles: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.PLATFORM_ADMIN,
      USER_ROLES.CORPORATE_ADMIN
    ],
    description: 'Corporate wellness programs'
  },
  
  // Team Management
  admin: {
    id: 'admin',
    name: 'Team Management',
    icon: Users,
    route: '/dashboard/admin',
    component: AdminModule,
    category: MODULE_CATEGORIES.MANAGEMENT,
    requiredRoles: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.PLATFORM_ADMIN,
      USER_ROLES.FEDERATION_ADMIN,
      USER_ROLES.CLUB_ADMIN,
      USER_ROLES.ACADEMY_ADMIN,
      USER_ROLES.CORPORATE_ADMIN,
      USER_ROLES.FACILITY_MANAGER
    ],
    description: 'Manage team members, invitations, and access codes'
  },
  
  // Media & Content
  media: {
    id: 'media',
    name: 'Media & Content',
    icon: Image,
    route: '/dashboard/media',
    component: MediaModule,
    category: MODULE_CATEGORIES.ENGAGEMENT,
    requiredRoles: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.PLATFORM_ADMIN,
      USER_ROLES.FEDERATION_ADMIN,
      USER_ROLES.CLUB_ADMIN,
      USER_ROLES.ACADEMY_ADMIN,
      USER_ROLES.CORPORATE_ADMIN
    ],
    description: 'Manage media assets, galleries, press releases, and live streams'
  },
  
  // Medical
  medical: {
    id: 'medical',
    name: 'Medical Center',
    icon: Heart,
    route: '/dashboard/medical',
    component: MedicalModule,
    category: MODULE_CATEGORIES.OPERATIONS,
    requiredRoles: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.PLATFORM_ADMIN,
      USER_ROLES.FEDERATION_ADMIN,
      USER_ROLES.CLUB_ADMIN,
      USER_ROLES.ACADEMY_ADMIN,
      USER_ROLES.MEDICAL_STAFF
    ],
    description: 'Manage athlete health, injuries, and medical clearances'
  },
  
  // Settings
  settings: {
    id: 'settings',
    name: 'Settings',
    icon: Settings,
    route: '/dashboard/settings',
    component: SettingsModule,
    category: MODULE_CATEGORIES.CORE,
    requiredRoles: Object.values(USER_ROLES),
    description: 'Account and preferences'
  },
  
  cms: {
    id: 'cms',
    name: 'CMS',
    icon: FileText,
    route: '/dashboard/cms',
    component: CMSModule,
    category: MODULE_CATEGORIES.MANAGEMENT,
    requiredRoles: [USER_ROLES.SUPER_ADMIN, USER_ROLES.PLATFORM_ADMIN],
    description: 'Website content management'
  },
  
  // Licensing & Compliance modules
  licensing: {
    id: 'licensing',
    name: 'Licensing',
    icon: FileCheck,
    route: '/dashboard/licensing',
    component: LicensingModule,
    category: MODULE_CATEGORIES.LICENSING,
    requiredRoles: [
      USER_ROLES.SUPER_ADMIN,
      USER_ROLES.PLATFORM_ADMIN,
      USER_ROLES.FEDERATION_ADMIN,
      USER_ROLES.CLUB_ADMIN,
      USER_ROLES.ACADEMY_ADMIN,
      USER_ROLES.LICENSING_OFFICER,
      USER_ROLES.REGULATORY_ADMIN,
      USER_ROLES.LICENSING_COMMITTEE
    ],
    description: 'License applications and compliance'
  }
};

// Role-based dashboard configurations
export const roleDashboards = {
  [USER_ROLES.SUPER_ADMIN]: {
    title: 'Super Admin Dashboard',
    modules: Object.keys(moduleRegistry),
    defaultWidgets: ['stats', 'events', 'revenue', 'users']
  },
  [USER_ROLES.PLATFORM_ADMIN]: {
    title: 'Platform Admin Dashboard',
    modules: Object.keys(moduleRegistry).filter(k => k !== 'parking'),
    defaultWidgets: ['stats', 'events', 'membership', 'news']
  },
  [USER_ROLES.FEDERATION_ADMIN]: {
    title: 'Federation Dashboard',
    modules: ['overview', 'analytics', 'events', 'tournaments', 'organizations', 'governance', 'licensing', 'membership', 'workforce', 'sponsorship', 'news', 'engagement', 'wallet', 'marketplace', 'media', 'medical', 'admin', 'settings'],
    defaultWidgets: ['events', 'membership', 'tournaments', 'sponsorship']
  },
  [USER_ROLES.CLUB_ADMIN]: {
    title: 'Club Dashboard',
    modules: ['overview', 'analytics', 'events', 'tournaments', 'facilities', 'licensing', 'membership', 'workforce', 'sponsorship', 'news', 'engagement', 'ticketing', 'wallet', 'marketplace', 'talent', 'media', 'medical', 'admin', 'settings'],
    defaultWidgets: ['events', 'membership', 'players', 'revenue']
  },
  [USER_ROLES.ACADEMY_ADMIN]: {
    title: 'Academy Dashboard',
    modules: ['overview', 'analytics', 'events', 'facilities', 'licensing', 'membership', 'academy', 'talent', 'wallet', 'marketplace', 'media', 'medical', 'admin', 'settings'],
    defaultWidgets: ['students', 'programs', 'events', 'revenue']
  },
  [USER_ROLES.CORPORATE_ADMIN]: {
    title: 'Corporate Sports Dashboard',
    modules: ['overview', 'analytics', 'events', 'facilities', 'membership', 'corporateSports', 'wallet', 'marketplace', 'media', 'admin', 'settings'],
    defaultWidgets: ['programs', 'participation', 'events', 'wellness']
  },
  [USER_ROLES.EVENT_MANAGER]: {
    title: 'Event Manager Dashboard',
    modules: ['overview', 'events', 'tournaments', 'ticketing', 'accreditation', 'visitors', 'sponsorship', 'wallet', 'settings'],
    defaultWidgets: ['events', 'tickets', 'accreditation', 'revenue']
  },
  [USER_ROLES.FACILITY_MANAGER]: {
    title: 'Facility Dashboard',
    modules: ['overview', 'analytics', 'facilities', 'workforce', 'visitors', 'parking', 'wallet', 'admin', 'settings'],
    defaultWidgets: ['bookings', 'occupancy', 'visitors', 'maintenance']
  },
  [USER_ROLES.VENDOR]: {
    title: 'Vendor Dashboard',
    modules: ['overview', 'marketplace', 'wallet', 'settings'],
    defaultWidgets: ['orders', 'products', 'revenue', 'reviews']
  },
  [USER_ROLES.FAN]: {
    title: 'Fan Dashboard',
    modules: ['overview', 'ticketing', 'membership', 'engagement', 'wallet', 'marketplace', 'settings'],
    defaultWidgets: ['tickets', 'points', 'polls', 'upcoming']
  },
  [USER_ROLES.ATHLETE]: {
    title: 'Athlete Dashboard',
    modules: ['overview', 'events', 'tournaments', 'membership', 'talent', 'wallet', 'marketplace', 'settings'],
    defaultWidgets: ['profile', 'events', 'stats', 'opportunities']
  },
  [USER_ROLES.COACH]: {
    title: 'Coach Dashboard',
    modules: ['overview', 'events', 'tournaments', 'academy', 'talent', 'wallet', 'settings'],
    defaultWidgets: ['players', 'sessions', 'events', 'progress']
  },
  // Licensing-specific roles
  [USER_ROLES.LICENSING_OFFICER]: {
    title: 'Licensing Officer Dashboard',
    modules: ['overview', 'licensing', 'organizations', 'governance', 'settings'],
    defaultWidgets: ['applications', 'compliance', 'inspections', 'alerts']
  },
  [USER_ROLES.COMPLIANCE_INSPECTOR]: {
    title: 'Compliance Inspector Dashboard',
    modules: ['overview', 'licensing', 'facilities', 'settings'],
    defaultWidgets: ['inspections', 'findings', 'schedule', 'reports']
  },
  [USER_ROLES.REGULATORY_ADMIN]: {
    title: 'Regulatory Authority Dashboard',
    modules: ['overview', 'analytics', 'licensing', 'organizations', 'governance', 'settings'],
    defaultWidgets: ['applications', 'licenses', 'compliance', 'alerts']
  },
  [USER_ROLES.LICENSING_COMMITTEE]: {
    title: 'Licensing Committee Dashboard',
    modules: ['overview', 'licensing', 'organizations', 'settings'],
    defaultWidgets: ['pending_decisions', 'appeals', 'meetings', 'reports']
  },
  [USER_ROLES.APPEALS_COMMITTEE]: {
    title: 'Appeals Committee Dashboard',
    modules: ['overview', 'licensing', 'settings'],
    defaultWidgets: ['appeals', 'hearings', 'decisions']
  }
};

// Helper function to get accessible modules for a user role
export const getAccessibleModules = (userRole, orgType = null) => {
  const roleConfig = roleDashboards[userRole] || roleDashboards[USER_ROLES.FAN];
  return roleConfig.modules
    .map(moduleId => moduleRegistry[moduleId])
    .filter(Boolean);
};

// Helper function to check if a user can access a module
export const canAccessModule = (moduleId, userRole) => {
  const module = moduleRegistry[moduleId];
  if (!module) return false;
  return module.requiredRoles.includes(userRole);
};

// Get modules grouped by category
export const getModulesByCategory = (userRole) => {
  const accessibleModules = getAccessibleModules(userRole);
  return accessibleModules.reduce((acc, module) => {
    if (!acc[module.category]) {
      acc[module.category] = [];
    }
    acc[module.category].push(module);
    return acc;
  }, {});
};

export default moduleRegistry;
